import Claim from './screens/Claim'
import { Navigate, Route, Routes } from 'react-router-dom'
import NewTourFlow from './screens/NewTourFlow'
import Artists from './screens/Artists'
import AgencyDashboard from './screens/AgencyDashboard'
import ArtistDetail from './screens/ArtistDetail'
import { useAuth } from './lib/auth'
import { useIsMobile } from './lib/useMediaQuery'
import SignIn from './screens/SignIn'
import Shell from './screens/Shell'
import Dashboard from './screens/Dashboard'
import Tours from './screens/Tours'
import TourDetail from './screens/TourDetail'
import ShowDetail from './screens/tour/ShowDetail'
import Account from './screens/Account'
import Database from './screens/Database'
import Export from './screens/Export'
import Splash from './components/Splash'
import Admin from './screens/admin/Admin'
import MobileToday from './screens/mobile/MobileToday'
import MobileTours from './screens/mobile/MobileTours'
import MobileTourHub from './screens/mobile/MobileTourHub'
import MobileShows from './screens/mobile/MobileShows'
import MobileItinerary from './screens/mobile/MobileItinerary'
import {
  MobileCrew,
  MobileTourSchedule,
  MobileTravel,
} from './screens/mobile/MobileTourPages'
import { useEntitlements } from './lib/entitlements'

/** The home screen the plan calls for. */
function Home({ isMobile }: { isMobile: boolean }) {
  const { permits } = useEntitlements()
  if (permits('roster_view') && !isMobile) return <AgencyDashboard />
  return isMobile ? <MobileToday /> : <Dashboard />
}

export default function App() {
  const { user, initialising, needsEmailVerification } = useAuth()
  // Phone layouts are different screens, not narrower ones — a tab bar, a
  // full-bleed poster, day-at-a-time lists. Branching here keeps each screen
  // honest instead of one component hiding half of itself at a breakpoint.
  const isMobile = useIsMobile()

  // "No user yet" and "signed out" look identical in state but must not look
  // identical on screen — rendering the sign-in form during the first auth
  // round trip flashes it at everyone who is already signed in.
  if (initialising) return <Splash />

  if (!user || needsEmailVerification) return <SignIn />

  return (
    <Routes>
      {/* Its own shell: the audience is one or two people and the data is every
          account, so it does not live behind the app's nav. */}
      <Route path="admin/*" element={<Admin />} />
      <Route path="app/claim" element={<Claim />} />

      <Route element={<Shell />}>
        {/* An agency opens Encore to ask "where is everyone", not "what's
            next for me". Different question, different home screen — chosen by
            the plan rather than by a setting nobody would find. */}
        <Route index element={<Home isMobile={isMobile} />} />
        <Route path="tours" element={isMobile ? <MobileTours /> : <Tours />} />
        {/* One screen per width inside each, rather than two routes — the
            roster is the same list either way, only the frame changes. */}
        <Route path="artists" element={<Artists />} />
        <Route path="artists/:artistId" element={<ArtistDetail />} />
        {/* Declared before the :tourId route so "new" is never read as an id.
            One screen for both widths — the flow is identical, only the frame
            tightens up. */}
        <Route path="tours/new" element={<NewTourFlow />} />
        <Route path="tours/:tourId" element={isMobile ? <MobileTourHub /> : <TourDetail />} />
        <Route
          path="tours/:tourId/shows"
          element={isMobile ? <MobileShows /> : <TourDetail />}
        />
        <Route path="tours/:tourId/shows/:showId" element={<ShowDetail />} />
        <Route
          path="tours/:tourId/schedule"
          element={isMobile ? <MobileTourSchedule /> : <TourDetail />}
        />
        <Route
          path="tours/:tourId/travel"
          element={isMobile ? <MobileTravel /> : <TourDetail />}
        />
        <Route path="tours/:tourId/crew" element={isMobile ? <MobileCrew /> : <TourDetail />} />
        <Route path="tours/:tourId/:tab" element={<TourDetail />} />
        <Route path="itinerary" element={<MobileItinerary />} />
        <Route path="database" element={<Database />} />
        <Route path="export" element={<Export />} />
        <Route path="account" element={<Account />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
